/**
 * Where a note lives (#14241, ADR-0112 § 7).
 *
 * `/notes/[id]` is the note; `/notes/[id]/transcript` is its transcript (the
 * page #13812 built, moved out of the meeting shell). Every link to either is
 * built here so a route rename is one edit, not a grep.
 *
 * Pure: no Prisma, no `server-only` — the meeting page's link card, the share
 * action and the transcript redirect all import it.
 */

import type { EvidenceQuote } from '@/lib/output-templates/stored-values'
import {
  TRANSCRIPT_MOMENT_PARAM,
  TRANSCRIPT_RECORDING_PARAM,
  TRANSCRIPT_SEGMENT_PARAM,
  transcriptSegmentAnchor,
} from '@/lib/meetings/transcript-view-model'

/** `/notes/<id>`. The id is encoded: it can arrive from a URL. */
export function notePath(noteId: string): string {
  return `/notes/${encodeURIComponent(noteId)}`
}

/**
 * `/notes/<id>/transcript`, optionally opened at a cited moment.
 *
 * `moment` is forwarded verbatim (the transcript page parses and validates
 * it); `null` opens the transcript at its start. `recordingId` names the
 * recording — the PART — the moment is on (#14427), and rides only with a
 * moment; without one the page resolves the moment in part 1.
 */
export function noteTranscriptPath(
  noteId: string,
  moment: string | null = null,
  recordingId: string | null = null,
): string {
  const base = `${notePath(noteId)}/transcript`
  if (moment === null) return base
  const query = new URLSearchParams({ [TRANSCRIPT_MOMENT_PARAM]: moment })
  if (recordingId !== null && recordingId.length > 0) {
    query.set(TRANSCRIPT_RECORDING_PARAM, recordingId)
  }
  return `${base}?${query.toString()}`
}

/**
 * `/notes/<id>/transcript?segment=<segmentId>#seg-<segmentId>` — a summary
 * claim's citation (#14378): the transcript opened scrolled to, and
 * highlighting, the first segment the claim cites. The query carries it for
 * the server-rendered highlight; the fragment lands the scroll before any
 * script runs.
 */
export function noteTranscriptSegmentPath(
  noteId: string,
  segmentId: string,
): string {
  const query = new URLSearchParams({ [TRANSCRIPT_SEGMENT_PARAM]: segmentId })
  return `${noteTranscriptPath(noteId)}?${query.toString()}#${encodeURIComponent(
    transcriptSegmentAnchor(segmentId),
  )}`
}

/** The note page's query param naming a passage to highlight (#16754). */
export const NOTE_HIGHLIGHT_PARAM = 'highlight'
/** The person the note's evidence banner is about (#16754). */
export const NOTE_EVIDENCE_FOR_PARAM = 'evidenceFor'
/** The element (principle) the note's evidence banner is for (#16754). */
export const NOTE_EVIDENCE_ELEMENT_PARAM = 'element'

/**
 * `/performance/<personId>` — a person's evaluation page (#16753), where
 * the note's evidence banner goes back to. Built from the id alone, never
 * from a URL a query carried.
 */
export function performancePersonPath(personId: string): string {
  return `/performance/${encodeURIComponent(personId)}`
}

/**
 * Where a piece of evidence opens (#16754) — the ONE builder of an evidence
 * link:
 *
 *   - a quote from a transcript segment opens the transcript at that
 *     segment (`noteTranscriptSegmentPath`);
 *   - any other opens its note with the passage highlighted
 *     (`/notes/<id>?highlight=<quote>`), and — given the person and
 *     element it is evidence for — the note's banner back to them.
 */
export function evidenceHref(
  evidence: Pick<EvidenceQuote, 'quote' | 'sourceNoteId' | 'segmentId'>,
  context?: { personId: string; principleId: string },
): string {
  if (evidence.segmentId !== null) {
    return noteTranscriptSegmentPath(evidence.sourceNoteId, evidence.segmentId)
  }
  const query = new URLSearchParams({ [NOTE_HIGHLIGHT_PARAM]: evidence.quote })
  if (context !== undefined) {
    query.set(NOTE_EVIDENCE_FOR_PARAM, context.personId)
    query.set(NOTE_EVIDENCE_ELEMENT_PARAM, context.principleId)
  }
  return `${notePath(evidence.sourceNoteId)}?${query.toString()}`
}

/**
 * `/api/notes/<id>/processing-status` — the note's processing-stage read,
 * which the note page polls (#14360).
 */
export function noteProcessingStatusApiPath(noteId: string): string {
  return `/api/notes/${encodeURIComponent(noteId)}/processing-status`
}

/**
 * Where a shared meeting record points (#13814), and so where the roster
 * claim it carries (#13815) runs.
 *
 * The NOTE when the meeting has one — that is where the record lives now —
 * and the meeting page otherwise: a meeting nobody recorded or summarised has
 * no note, and its page is still the record of the run.
 */
export function sharedRecordPath(input: {
  meetingId: string
  noteId: string | null
}): string {
  return input.noteId !== null
    ? notePath(input.noteId)
    : `/meetings/${encodeURIComponent(input.meetingId)}`
}
